import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Product } from './index';
import { categoryService } from '../../services/categoryService';
import ProductMetaTags from './ProductMetaTags';
import VideoModal from './VideoModal';
import ImageZoomModal from './ImageZoomModal';
import './ProductDetail.css';
import RelatedProducts from './RelatedProducts';
import logo from '../../assets/images/logo-blue.png';
import { productService } from '../../services/productService'; // Make sure this import exists
const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isImageZoomOpen, setIsImageZoomOpen] = useState(false);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);

  // Fetch the product data based on ID
 useEffect(() => {
   const fetchProduct = async () => {
     setLoading(true);
     try {
       console.log('🔍 Fetching product with ID:', id);

       // Use productService instead of direct fetch - this will handle localStorage
       const data = await productService.getProductById(id || '');

       if (data) {
         console.log('✅ Product fetched successfully:', data.titleEn);
         setProduct(data);
         setError(null);

         // Double-check that it was saved to localStorage
         const visitedProducts = productService.visitedProducts.getVisitedProducts();
         console.log('📦 Visited products in localStorage:', visitedProducts.length);
       } else {
         console.log('❌ Product not found');
         setError('Product not found');
       }
     } catch (err) {
       console.error('❌ Error fetching product:', err);
       setError('Product not found. Please try again later.');

       // For development/demo purposes - keep your existing fallback logic
       if (window.location.hostname === 'localhost') {
         const storedProducts = localStorage.getItem('sampleProducts');
         if (storedProducts) {
           const products = JSON.parse(storedProducts);
           const foundProduct = products.find((p: any) => p.id === parseInt(id || '0'));
           if (foundProduct) {
             setProduct(foundProduct);
             setError(null);
             // Also add this to visited products
             productService.visitedProducts.addVisited(foundProduct);
           }
         }
       }
     } finally {
       setLoading(false);
     }
   };

   if (id) {
     fetchProduct();
   }
 }, [id]);

  // Navigation between images
  const nextImage = () => {
    if (product && product.images.length > 0) {
      setCurrentImageIndex((prevIndex) =>
        (prevIndex + 1) % product.images.length
      );
    }
  };

  const prevImage = () => {
    if (product && product.images.length > 0) {
      setCurrentImageIndex((prevIndex) =>
        (prevIndex - 1 + product.images.length) % product.images.length
      );
    }
  };

  // Set specific image by index
  const setImage = (index: number) => {
    if (product && index >= 0 && index < product.images.length) {
      setCurrentImageIndex(index);
    }
  };

  // Open video modal
  const openVideoModal = () => {
    if (product?.videoUrl) {
      setIsVideoModalOpen(true);
    }
  };

  // Close video modal
  const closeVideoModal = useCallback(() => {
    setIsVideoModalOpen(false);
  }, []);

  // Open image zoom modal
  const openImageZoom = useCallback((imageIndex?: number) => {
    if (product && product.images.length > 0) {
      setZoomImageIndex(imageIndex !== undefined ? imageIndex : currentImageIndex);
      setIsImageZoomOpen(true);
    }
  }, [product, currentImageIndex]);

  // Close image zoom modal
  const closeImageZoom = useCallback(() => {
    setIsImageZoomOpen(false);
  }, []);

  // Handle "Buy Now" button click - Updated with cache busting
  const handleBuyNowClick = () => {
    if (!product) return;

    const productName = language === 'ar' ? product.titleAr : product.titleEn;
    const baseUrl = window.location.origin;
    const productPath = window.location.pathname;
    const productUrl = `${baseUrl}${productPath}`;

    // Add cache buster to help WhatsApp fetch fresh meta tags
    const cacheBustedUrl = `${productUrl}?v=${Date.now()}`;

    const message = `Hello, I'm interested in purchasing "${productName}" for ${product.price} NIS from AI Academy.\n\nProduct link: ${cacheBustedUrl}`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappLink = `https://wa.me/972548788550?text=${encodedMessage}`;

    // Try to refresh WhatsApp preview (optional)
    try {
      // Pre-warm the URL for WhatsApp
      fetch(productUrl, { method: 'HEAD', mode: 'no-cors' }).catch(() => {});
    } catch (error) {
      // Ignore errors
    }

    window.open(whatsappLink, '_blank');
  };

  // Social media sharing - Updated with cache busting
  const getShareLinks = () => {
    if (!product) return null;

    const baseUrl = window.location.origin;
    const productPath = window.location.pathname;
    const currentUrl = `${baseUrl}${productPath}`;
    const cacheBustedUrl = `${currentUrl}?share=${Date.now()}`;

    const productName = language === 'ar' ? product.titleAr : product.titleEn;
    const productDescription = language === 'ar' ? product.descriptionAr : product.descriptionEn;

    // Function to strip HTML tags for sharing plain text
    const stripHtml = (html: string) => {
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return temp.textContent || temp.innerText || '';
    };

    // Strip HTML from description for sharing
    const plainDescription = stripHtml(productDescription || '');

    return {
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${productName}\n${cacheBustedUrl}`)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(cacheBustedUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(productName)}&url=${encodeURIComponent(cacheBustedUrl)}`,
      email: `mailto:?subject=${encodeURIComponent(productName)}&body=${encodeURIComponent(`${plainDescription}\n\nCheck out this product: ${cacheBustedUrl}`)}`
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="product-detail-container">
        <div className="product-detail-loading">
          <div className="loading-spinner"></div>
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !product) {
    return (
      <div className="product-detail-container">
        <div className="product-detail-error">
          <h2>Product Not Found</h2>
          <p>{error}</p>
          <Link to="/shop" className="back-to-shop-btn">
            {language === 'ar' ? 'العودة إلى المتجر' : 'Back to Shop'}
          </Link>
        </div>
      </div>
    );
  }

  // No product found
  if (!product) {
    return (
      <div className="product-detail-container">
        <div className="product-detail-error">
          <h2>{language === 'ar' ? 'الأداة غير موجودة' : 'Product Not Found'}</h2>
          <Link to="/shop" className="back-to-shop-btn">
            {language === 'ar' ? 'العودة إلى المتجر' : 'Back to Shop'}
          </Link>
        </div>
      </div>
    );
  }

  // Get the category name based on the product's category ID and current language
  const getCategoryName = () => {
    return categoryService.getCategoryName(product.category, language as 'en' | 'ar');
  };

  const shareLinks = getShareLinks();

  return (
    <>
      {/* Add dynamic meta tags for social sharing */}
      <ProductMetaTags product={product} />
        <div className="shop-logo-container">
         <a href="https://www.aiaj.xyz/shop"> <img src={logo} alt="AI Academy Logo" className="shop-logo" /></a>
        </div>
      <div className="product-detail-container">
        <div className="product-detail-breadcrumb">
          <Link to="/shop">
            {language === 'ar' ? 'الصفحة الرئيسية' : 'Shop'}
          </Link>
          <span className="breadcrumb-separator">&gt;</span>
          <Link
            to={`/shop?category=${product.category}`}
            className="breadcrumb-link"
          >
            {getCategoryName()}
          </Link>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="current-page">
            {language === 'ar' ? product.titleAr : product.titleEn}
          </span>
        </div>

        <div className="product-detail-content">
          <div className="product-detail-gallery">
            <div className="main-image-container">
              {product.images && product.images.length > 0 ? (
                <>
                  <img
                    src={product.images[currentImageIndex].url}
                    alt={product.images[currentImageIndex].alt}
                    className="main-product-image"
                    onClick={() => openImageZoom(currentImageIndex)}
                    style={{ cursor: 'zoom-in' }}
                  />

                  {product.images.length > 1 && (
                    <>
                      <button className="gallery-nav prev" onClick={prevImage} aria-label="Previous image">❮</button>
                      <button className="gallery-nav next" onClick={nextImage} aria-label="Next image">❯</button>

                      <div className="image-counter">
                        {currentImageIndex + 1} / {product.images.length}
                      </div>
                    </>
                  )}

                  {/* Zoom button */}
                  <button
                    className="zoom-button"
                    onClick={() => openImageZoom(currentImageIndex)}
                    aria-label="Zoom image"
                    title={language === 'ar' ? 'تكبير الصورة' : 'Zoom image'}
                  >
                    <ZoomIcon />
                  </button>

                  {/* Video button - only show if product has video URL */}
                  {product.videoUrl && (
                    <button
                      className="video-play-button"
                      onClick={openVideoModal}
                      aria-label="Play product video"
                    >
                      <VideoIcon />
                    </button>
                  )}
                </>
              ) : (
                <div className="no-image-placeholder">
                  {language === 'ar' ? 'لا توجد صورة' : 'No Image Available'}
                </div>
              )}
            </div>

            {product.images && product.images.length > 1 && (
              <div className="thumbnail-gallery">
                {product.images.map((image, index) => (
                  <div
                    key={image.id}
                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setImage(index)}
                  >
                    <img src={image.url} alt={image.alt} />
                    <div
                      className="thumbnail-zoom-overlay"
                      onClick={(e) => {
                        e.stopPropagation();
                        openImageZoom(index);
                      }}
                    >
                      <ZoomIcon />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="product-detail-info">
            <h1 className="product-title">
              {language === 'ar' ? product.titleAr : product.titleEn}
            </h1>

            <div className="product-meta">
              <div className="product-price">{product.price} ₪</div>
              <div className="product-age-range">
                {language === 'ar'
                  ? `الفئة العمرية: ${product.ageRangeAr}`
                  : `Age Range: ${product.ageRangeEn}`}
              </div>

              <div className="product-category">
                {language === 'ar'
                  ? `الفئة: ${categoryService.getCategoryNameAr(product.category)}`
                  : `Category: ${categoryService.getCategoryNameEn(product.category)}`}
              </div>
                <span className="vat-disclaimer">
                  {language === 'ar' ? 'الأسعار غير شاملة ضريبة القيمة المضافة' : 'Prices exclude VAT'}
                </span>
            </div>

            <div className="product-description">
              <h2>{language === 'ar' ? 'وصف الأداة' : 'Product Description'}</h2>
              {/* Render HTML content safely */}
              <div
                dangerouslySetInnerHTML={{
                  __html: language === 'ar' ? product.descriptionAr || '' : product.descriptionEn || ''
                }}
                className="product-description-html"
              />
            </div>

            <div className="product-actions">
              <button className="buy-now-button" onClick={handleBuyNowClick}>
                {language === 'ar' ? 'اطلبه الآن من خلال واتساب' : 'Buy Now via WhatsApp'}
              </button>

              {shareLinks && (
                <div className="product-share">
                  <span className="share-label">
                    {language === 'ar' ? 'مشاركة:' : 'Share:'}
                  </span>
                  <div className="share-buttons">
                    <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp">
                      <WhatsAppIcon />
                    </a>
                    <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook">
                      <FacebookIcon />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

    <RelatedProducts
          currentProductId={product.id.toString()}
          category={product.category}
          language={language}
        />
        <div className="back-to-shop">
          <Link to="/shop" className="back-to-shop-btn">
            {language === 'ar' ? '← العودة إلى المتجر' : 'Back to Shop ←'}
          </Link>
        </div>

        {/* Video Modal */}
        {isVideoModalOpen && product.videoUrl && (
          <VideoModal
            videoUrl={product.videoUrl}
            onClose={closeVideoModal}
            title={language === 'ar' ? product.titleAr : product.titleEn}
          />
        )}

        {/* Image Zoom Modal */}
        {isImageZoomOpen && product.images && product.images.length > 0 && (
          <ImageZoomModal
            images={product.images}
            currentIndex={zoomImageIndex}
            onClose={closeImageZoom}
            productTitle={language === 'ar' ? product.titleAr : product.titleEn}
          />
        )}
      </div>
    </>
  );
};

// Social Media Icons
const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#25D366">
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Video Icon
const VideoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#FFF">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
  </svg>
);

// Zoom Icon
const ZoomIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
    <path d="M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z"/>
  </svg>
);

export default ProductDetail;